import { ListeTable, ListeTableContainer } from '@mairistem/liste';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { TYPES_PRODUIT } from '@/constants';
import { ProduitContext } from '@/contexts';
import { deleteProduit, postProduit, putProduit } from '@/routes';
import type { ProduitType } from '@/types';
import type { ProduitsListeItemDetailType } from '@/types/produit';
import { formatProduitsToListeItemDetail } from '@/utils';
import { parseApiValidationErrors } from '@/utils/apiErrors';
import ProduitsListeTableRowDetail from './ProduitsListeTableRowDetail';

type ProduitsListeDetailProps = {
  editable?: boolean;
  produit?: ProduitType;
  localItems?: ProduitType[];
  onLocalAdd?: (item: ProduitType) => boolean;
  onLocalEdit?: (item: ProduitType) => boolean;
  onLocalDelete?: (item: ProduitType) => boolean;
};

const ProduitsListeDetail = (props: ProduitsListeDetailProps) => {
  const { editable = false, produit, localItems, onLocalAdd, onLocalEdit, onLocalDelete } = props;

  const isLocalMode = !_.isNil(localItems);

  const {
    produits: contextProduits,
    loading: contextLoading,
    refresh,
  } = React.useContext(ProduitContext);

  // Produit sans données d'imputation (tous les champs vides)
  const emptyProduit = React.useMemo(
    () =>
      _.find(
        contextProduits,
        (p) =>
          !p.fonction &&
          !p.ventilation &&
          !p.compteAnalytique &&
          !p.compteAnalytique2 &&
          !p.siret &&
          !p.codeCollectivite &&
          !p.codeBudget,
      ),
    [contextProduits],
  );

  // Filtre les produits sans données d'imputation pour l'affichage
  const filteredContextProduits = React.useMemo(
    () =>
      _.filter(
        contextProduits,
        (p) =>
          p.fonction ||
          p.ventilation ||
          p.compteAnalytique ||
          p.compteAnalytique2 ||
          p.siret ||
          p.codeCollectivite ||
          p.codeBudget,
      ),
    [contextProduits],
  );

  const produits = isLocalMode ? localItems : filteredContextProduits;
  const loading = isLocalMode ? false : contextLoading;

  // Formate les données pour l'affichage dans la liste
  // avec seulement les data dont on a besoin
  const formatedProduits = React.useMemo<ProduitsListeItemDetailType[]>(
    () => formatProduitsToListeItemDetail(produits),
    [produits],
  );

  const handleValidateClick = React.useCallback(
    async (item, status) => {
      if (isLocalMode) {
        if (status === 'updating') {
          return onLocalEdit(item);
        }
        return onLocalAdd(item);
      }

      try {
        if (status === 'updating') {
          await request.put(putProduit(item.identifiant), {
            ...item,
            type: TYPES_PRODUIT.AU_COMPTANT,
          });
          toast('Imputation modifiée avec succès', { type: 'success' });
          refresh();
          return item;
        }

        // status === 'creating'
        // Si un produit sans imputation existe, on le met à jour au lieu d'en créer un nouveau
        if (emptyProduit) {
          const updatedItem = {
            ...item,
            identifiant: emptyProduit.identifiant,
            type: TYPES_PRODUIT.AU_COMPTANT,
          };
          await request.put(putProduit(emptyProduit.identifiant), updatedItem);
          toast('Imputation ajoutée avec succès', { type: 'success' });
          refresh();
          return updatedItem;
        }

        const result = await request.post(postProduit(), {
          ...item,
          type: TYPES_PRODUIT.AU_COMPTANT,
        });
        toast('Imputation ajoutée avec succès', { type: 'success' });
        refresh();
        return result;
      } catch (error: any) {
        const apiErrors = parseApiValidationErrors(error?.response?.data);
        const message = !_.isEmpty(apiErrors)
          ? _.head(apiErrors).message
          : "Erreur lors de la sauvegarde de l'imputation";
        toast(message, { type: 'error' });
        return false;
      }
    },
    [isLocalMode, onLocalAdd, onLocalEdit, emptyProduit, refresh],
  );

  const handleDeleteClick = React.useCallback(
    async (item) => {
      if (isLocalMode) {
        return onLocalDelete(item);
      }

      try {
        // Si on supprime l'imputation du produit sur lequel on est placé dans la modal,
        // on vide les champs d'imputation au lieu de supprimer le produit
        if (item.identifiant === produit?.identifiant) {
          await request.put(putProduit(item.identifiant), {
            ...item,
            fonction: '',
            ventilation: '',
            compteAnalytique: '',
            compteAnalytique2: '',
            siret: '',
            codeCollectivite: '',
            codeBudget: '',
            type: TYPES_PRODUIT.AU_COMPTANT,
          });
        } else {
          await request.delete(deleteProduit(item.identifiant));
        }
        toast('Imputation supprimée avec succès', { type: 'success' });
        refresh();
        return true;
      } catch {
        toast("Erreur lors de la suppression de l'imputation", { type: 'error' });
        return false;
      }
    },
    [isLocalMode, onLocalDelete, produit, refresh],
  );

  // Article et code produit local sont désormais saisis dans la ligne d'imputation :
  // on n'injecte plus que les champs de niveau produit (libellé, tva).
  const handleAddClick = React.useCallback(
    (newItem) => ({
      ...newItem,
      libelle: produit?.libelle ?? '',
      tva: produit?.tva ?? 0,
    }),
    [produit],
  );

  const renderRow = React.useCallback(
    (item, __, rowProps, utilities?) => (
      <ProduitsListeTableRowDetail
        item={item}
        rowProps={rowProps}
        utilities={editable ? utilities : undefined}
      />
    ),
    [editable],
  );

  const columns = (
    <ListeTable.Header>
      <ListeTable.Column verticalAlign="middle" name="article" style={{ width: 130 }}>
        Article
      </ListeTable.Column>
      <ListeTable.Column verticalAlign="middle" name="codeProduitLocal" style={{ width: 130 }}>
        Code
        <br />
        produit local
      </ListeTable.Column>
      <ListeTable.Column verticalAlign="middle" name="fonction" style={{ width: 130 }}>
        Fonction
      </ListeTable.Column>
      <ListeTable.Column verticalAlign="middle" name="compteAnalytique" style={{ width: 170 }}>
        Analytique 1
      </ListeTable.Column>
      <ListeTable.Column verticalAlign="middle" name="compteAnalytique2" style={{ width: 170 }}>
        Analytique 2
      </ListeTable.Column>
      <ListeTable.Column verticalAlign="middle" name="ventilation" style={{ width: 170 }}>
        Ventilation
      </ListeTable.Column>
      <ListeTable.Column verticalAlign="middle" name="siret" style={{ width: 130 }}>
        Siret
      </ListeTable.Column>
      <ListeTable.Column verticalAlign="middle" name="codeCollectivite" style={{ width: 130 }}>
        Code
        <br />
        collectivité
      </ListeTable.Column>
      <ListeTable.Column verticalAlign="middle" name="codeBudget" style={{ width: 130 }}>
        Code budget
      </ListeTable.Column>
    </ListeTable.Header>
  );

  if (editable) {
    return (
      <div style={{ height: 450 }}>
        <ListeTableContainer
          items={produits}
          onValidateClick={handleValidateClick}
          onDeleteClick={handleDeleteClick}
          onAddClick={handleAddClick}
          allowModifications
          allowDeletions
          // Un produit au comptant ne peut avoir qu'une seule imputation
          allowAdditions={_.size(produits) === 0}
          additionLabel="Ajouter une imputation"
        >
          <ListeTable loading={loading}>
            {columns}
            {renderRow as unknown as React.ReactNode}
          </ListeTable>
        </ListeTableContainer>
      </div>
    );
  }

  return (
    <div style={{ height: 450 }}>
      <ListeTable count={_.size(formatedProduits)} items={formatedProduits} loading={loading}>
        {columns}
        {renderRow as unknown as React.ReactNode}
      </ListeTable>
    </div>
  );
};

export default ProduitsListeDetail;
